import React, { useState, useEffect } from 'react';
import { Tree, message, Card, Badge } from 'antd';
import { Row, Col } from 'react-bootstrap';
import { getReportConfigDetails, getReportTemplateDetailsById, getAllReportTemplatesByType } from '../../../services/ReportTemplateService';
import InlineFilterDropdown from './InlineFilterDropdown';

//Format column mapping data for storage in the database
export const buildColumnMapping = (columnsObj) => {
  const result = {};
  Object.entries(columnsObj).forEach(([table, columns]) => {
    const sorted = [...columns].sort((a, b) => a.order - b.order);
    result[table] = sorted.map((col) => col.column);
  });
  return result;
};

//Convert simple column array mapping to ordered column objects
export const convertMappingToOrderedColumns = (columnMapping) => {
  const formatted = {};
  if (!columnMapping) return formatted;
  
  Object.entries(columnMapping).forEach(([table, columns]) => {
    formatted[table] = columns.map((col, idx) => ({
      column: col,
      order: idx + 1,
    }));
  });
  return formatted;
};

// Normalize column name by removing alias
const normalizeColumnName = (col) => {
  return col.split(" as ")[0].trim();
};

export const processCheckedKeys = (checkedKeysValue, sheetName, prevColumns, prevFilters) => {
  const prevCols = prevColumns[sheetName] || [];

  // Only include columns (not the table name itself) as columns
  const newCols = checkedKeysValue
    .filter((key) => key !== sheetName)
    .map((colKey) => {
      const existing = prevCols.find((c) => c.column === colKey);
      return {
        column: colKey,
        order: existing?.order ?? null,
      };
    });

  // Sort by previous order (if any), then reassign orders 1...n
  newCols.sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));
  newCols.forEach((col, idx) => (col.order = idx + 1));

  // Determine which columns were unchecked
  const newColKeys = newCols.map(c => normalizeColumnName(c.column));
  const prevColKeys = prevCols.map(c => normalizeColumnName(c.column));
  const uncheckedColumns = prevColKeys.filter(key => !newColKeys.includes(key));

  // Remove filters for unchecked columns
  const updatedFilters = { ...prevFilters };
  if (uncheckedColumns.length > 0 && updatedFilters[sheetName]) {
    uncheckedColumns.forEach(colKey => {
      delete updatedFilters[sheetName][colKey];
    });
    
    // Remove table entry if no filters remain
    if (Object.keys(updatedFilters[sheetName]).length === 0) {
      delete updatedFilters[sheetName];
    }
  }

  return { newCols, updatedFilters };
};

// Handles drag and drop reordering within a table
export const handleDragReorder = (sheetName, sourceColumn, targetColumn, prevColumns) => {
  const tableColumns = prevColumns[sheetName] || [];
  const sourceItem = tableColumns.find(item => item.column === sourceColumn);
  const targetItem = tableColumns.find(item => item.column === targetColumn);
  
  if (!sourceItem || !targetItem) return prevColumns;

  const sourceOrder = sourceItem.order;
  const targetOrder = targetItem.order;
  
  // Create new array with insertion logic
  const newColumns = tableColumns.map(item => {
    if (item.column === sourceColumn) {
      // Source takes target's position
      return { ...item, order: targetOrder };
    }
    
    // Shift other items based on direction of movement
    if (sourceOrder < targetOrder) {
      // Moving down: shift items between source and target up
      if (item.order > sourceOrder && item.order <= targetOrder && item.column !== sourceColumn) {
        return { ...item, order: item.order - 1 };
      }
    } else {
      // Moving up: shift items between target and source down
      if (item.order >= targetOrder && item.order < sourceOrder && item.column !== sourceColumn) {
        return { ...item, order: item.order + 1 };
      }
    }
    
    return item;
  });

  return {
    ...prevColumns,
    [sheetName]: newColumns
  };
};

//Removes prefix (n1) from column name for display
export const getDisplayName = (columnExpr) => {
  // Extract alias col name if present
  const aliasMatch = columnExpr.match(/\s+as\s+"(.+?)"$/i) ||  columnExpr.match(/^"(.*?)"$/);
  if (aliasMatch) return aliasMatch[1]

  // Fallback if no alias, remove prefix if present
  const parts = columnExpr.split('.');
  return parts.length > 1 ? parts.slice(1).join('.') : columnExpr;
};

// Draggable Column Card Component
function DraggableColumnCard({
  sheetName, 
  tableObj, 
  checkedColumns, 
  filters = {}, 
  onCheck, 
  onFilterChange, 
  disabled, 
  columnDragState, 
  onColumnDragStart, 
  onColumnDragOver, 
  onColumnDrop, 
  onColumnDragEnd, 
  billType,
  viewMode = false
}) {
  const tableCheckedColumns = checkedColumns[sheetName] || [];
  const tableFilters = filters[sheetName] || {};
  const checkedCount = tableCheckedColumns.length;
  const totalColumns = tableObj.columns.length;

  // Get checked column keys for tree
  const checkedColumnKeys = tableCheckedColumns.map(item => item.column);
  const allSelected = checkedCount === totalColumns;

  // If all columns are selected, include table name in checked keys
  const treeCheckedKeys = allSelected ? [sheetName, ...checkedColumnKeys] : checkedColumnKeys;

  // Check if a column is filterable
  const isFilterableColumn = (columnName) => {
    return tableObj.filterable_columns?.includes(normalizeColumnName(columnName));
  };

  // Create tree data with drag & drop columns
  const createTreeData = () => {
    // Sort columns: checked first (by order), then unchecked
    const sortedColumns = [
      ...tableCheckedColumns
        .sort((a, b) => a.order - b.order)
        .map(item => ({ column: item.column, checked: true, order: item.order })),
      ...tableObj.columns
        .filter(col => !tableCheckedColumns.some(item => item.column === col))
        .map(col => ({ column: col, checked: false, order: null }))
    ];

    const columnNodes = sortedColumns.map(({ column, checked, order }) => {
      const isFilterable = isFilterableColumn(column);
      const normalizedColumn = normalizeColumnName(column);
      const filterValue = tableFilters[normalizedColumn];

      return {
        title: (
          <div
            draggable={checked && !disabled && !viewMode}
            onDragStart={(e) => {
              e.stopPropagation();
              e.dataTransfer.effectAllowed = 'move';
              onColumnDragStart(sheetName, column);
            } }
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.dataTransfer.dropEffect = 'move';
              onColumnDragOver(sheetName, column);
            } }
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onColumnDrop(sheetName, column);
            } }
            onDragEnd={onColumnDragEnd}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '4px 8px',
              borderRadius: '4px',
              flexWrap: 'wrap',
              opacity: columnDragState.dragging?.sheetName === sheetName && columnDragState.dragging?.column === column ? 0.5 : 1,
              transform: columnDragState.dragging?.sheetName === sheetName && columnDragState.dragging?.column === column ? 'scale(0.95)' : 'scale(1)',
              boxShadow: columnDragState.dropTarget?.sheetName === sheetName && columnDragState.dropTarget?.column === column ? '0 0 0 2px #52c41a' : 'none',
              cursor: checked && !disabled && !viewMode ? 'grab' : 'default',
              transition: 'all 0.15s ease'
            }}
          >
            {checked && (
              <Badge
                count={order}
                overflowCount={999}
                style={{ backgroundColor: '#1890ff', fontSize: '10px', minWidth: '16px', height: '16px', lineHeight: '16px' }}
              />
            )}
            <span style={{ fontSize: '13px', fontWeight: checked ? 500 : 400, color: checked ? 'black' : '#666', whiteSpace: 'normal', wordBreak: 'break-word', flex: 1}}>
              {getDisplayName(column)}
            </span>
    
            {isFilterable && (
            <InlineFilterDropdown
              sheetName={sheetName}
              columnName={normalizedColumn}
              value={filterValue}
              onChange={(value) => onFilterChange(sheetName, normalizedColumn, value)}
              disabled={disabled || viewMode}
              viewMode={viewMode}
              billType={billType}
            />
          )}
          </div>
        ),
        key: column,
      };
    });

    return [{
      title: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <span style={{ fontWeight: 600, color: '#1890ff' }}>{sheetName}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '11px', color: '#999', marginLeft: '4px' }}>
              ({checkedCount} of {totalColumns})
            </span>
          </div>
        </div>
      ),
      key: sheetName,
      children: columnNodes,
    }];
  };

  return (
    <div>
      <Card
        size="small"
        style={{
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: checkedCount > 0 ? '1px solid #1890ff' : '1px solid #d9d9d9',
        }}
      >
        <div style={{ position: 'relative' }}>
          <Tree
            checkable
            disabled={disabled || viewMode}
            onCheck={(checkedKeysValue) => onCheck(checkedKeysValue, sheetName)}
            checkedKeys={treeCheckedKeys}
            treeData={createTreeData()} />
        </div>
      </Card>
    </div>
  );
}

export const useReportTemplate = ({ type_name, templateId = null, visible }) => {
  const [loading, setLoading] = useState(false);
  const [treeData, setTreeData] = useState(null);
  const [checkedColumns, setCheckedColumns] = useState({});
  const [existingTemplates, setExistingTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [filters, setFilters] = useState({});
  const [initialValues, setInitialValues] = useState({
    templateName: '',
    description: '',
  });

  // Load data when modal becomes visible
  useEffect(() => {
    if (visible && type_name) {
      if (templateId) {
        fetchTemplateById();
        setEditMode(false);
      } else {
        fetchConfigDetails();
      }
      fetchExistingTemplates();
    }
  }, [visible, type_name, templateId]);


  // Fetch template details with selected columns and sheets when view details is clicked
  const fetchTemplateById = async () => {
    setLoading(true);
    try {
      const response = await getReportTemplateDetailsById(templateId);
      if (response.template) {
        setInitialValues({
          templateName: response.template.template_name || '',
          description: response.template.description || '',
        });
        setTreeData(response.tree);
        
        // Convert simple column mapping to ordered structure
        const orderedColumns = convertMappingToOrderedColumns(response.template.column_mapping);
        setCheckedColumns(orderedColumns);

        // Load filters if present
        if (response.template.col_filters) {
          setFilters(response.template.col_filters);
        }
      }
    } catch (error) {
      console.error('Error fetching template details:', error);
      message.error('Failed to fetch template details');
    } finally {
      setLoading(false);
    }
  };


  // Fetch full configuration details (entire tree with all sheets and columns) for the bill type
  const fetchConfigDetails = async () => {
    setLoading(true);
    try {
      const response = await getReportConfigDetails(type_name);
      if (response && response[type_name]) {
        setTreeData(response[type_name]);
      }
    } catch (error) {
      console.error('Error fetching configuration details:', error);
      message.error('Failed to fetch configuration details');
    } finally {
      setLoading(false);
    }
  };


  // Fetch all templates for the current bill type so that admin can  duplicate from any existing template
  const fetchExistingTemplates = async () => {
    try {
      const response = await getAllReportTemplatesByType(type_name);
      if (response && response.reportemplates) {
        setExistingTemplates(response.reportemplates);
      } else {
        setExistingTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      message.error('Failed to fetch templates');
    }
  };

  // Switch to edit mode (will fetch full tree data so that admin can select any columns or sheets)
  const switchToEditMode = async () => {
    setEditMode(true);
    await fetchConfigDetails();
  };

  // Cancel edit mode and revert to view mode
  const cancelEditMode = () => {
    setEditMode(false);
    fetchTemplateById();
  };

  // Handle column selection changes
  const onCheck = (checkedKeysValue, sheetName) => {
    setCheckedColumns(prevColumns => {
      const { newCols, updatedFilters } = processCheckedKeys(
        checkedKeysValue, 
        sheetName, 
        prevColumns,
        filters
      );
      
      // Update filters separately
      setFilters(updatedFilters);
      
      const updated = { ...prevColumns };
      if (newCols.length > 0) {
        updated[sheetName] = newCols;
      } else {
        delete updated[sheetName];
      }
      
      return updated;
    });
  };

  // Handle drag and drop column reordering
  const onColumnReorder = (sheetName, sourceColumn, targetColumn) => {
    setCheckedColumns(prevColumns => 
      handleDragReorder(sheetName, sourceColumn, targetColumn, prevColumns)
    );
  };

  // Handle selection of an existing template
  const onTemplateSelect = (templateId) => {
    const selected = existingTemplates.find(t => t.id === templateId);
    setSelectedTemplate(selected?.template_name || null);
    
    if (selected?.column_mapping) {
      const formatted = convertMappingToOrderedColumns(selected.column_mapping);
      setCheckedColumns(formatted);
    } else {
      setCheckedColumns({});
    }

    // Load filters from selected template
    if (selected?.col_filters) {
      setFilters(selected.col_filters);
    } else {
      setFilters({});
    }
  };

  // Handle filter value change for a specific column
  const onFilterChange = (sheetName, columnName, value) => {
  setFilters(prev => {
    const updated = { ...prev };
    const isEmpty = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
    // handle empty filter or column deselection
    if (isEmpty) {
      if (updated[sheetName]) {
        delete updated[sheetName][columnName];

        if (Object.keys(updated[sheetName]).length === 0) {
          delete updated[sheetName];
        }
      }
    } else {
      // Add/update the filter only if value is not empty
      if (!updated[sheetName]) {
        updated[sheetName] = {};
      }
      updated[sheetName][columnName] = value;
    }
    
    return updated;
  });
};

// ensure column selection on filter selection
const validateFilters = () => {
  const errors = [];
  
  Object.entries(filters).forEach(([sheetName, sheetFilters]) => {
    const checkedCols = checkedColumns[sheetName] || [];
    const checkedColNames = checkedCols.map(c => normalizeColumnName(c.column));
    
    Object.keys(sheetFilters).forEach(filterCol => {
      if (!checkedColNames.includes(filterCol)) {
        errors.push(`Filter on "${filterCol}" in "${sheetName}" requires the column to be selected`);
      }
    });
  });
  
  return errors;
};


  // Reset state
  const resetState = () => {
    setCheckedColumns({});
    setFilters({});
    setSelectedTemplate(null);
    setInitialValues({
      templateName: '',
      description: '',
    });
  };

  return {
    loading,
    treeData,
    checkedColumns,
    filters,
    existingTemplates,
    selectedTemplate,
    editMode,
    initialValues,
    onCheck,
    onColumnReorder,
    onFilterChange,
    onTemplateSelect,
    switchToEditMode,
    cancelEditMode,
    resetState,
    fetchConfigDetails,
    fetchTemplateById,
    validateFilters
  };
};

// Renders a tree structure with drag and drop for the bill type's sheets and columns
export const ReportTreeRenderer = ({
  treeData,
  checkedColumns,
  filters = {},
  onCheck,
  onColumnReorder,
  onFilterChange,
  disabled = false,
  viewMode = false,
  billType,
}) => {
  const [columnDragState, setColumnDragState] = useState({
    dragging: null,
    dropTarget: null
  });

  if (!treeData) return null;

  // Column drag handlers
  const handleColumnDragStart = (sheetName, column) => {
    setColumnDragState(prev => ({ ...prev, dragging: { sheetName, column } }));
  };

  const handleColumnDragOver = (sheetName, column) => {
    if (columnDragState.dragging && 
        columnDragState.dragging.sheetName === sheetName && 
        columnDragState.dragging.column !== column) {
      setColumnDragState(prev => ({ ...prev, dropTarget: { sheetName, column } }));
    }
  };

  const handleColumnDrop = (sheetName, column) => {
    if (columnDragState.dragging && columnDragState.dragging.sheetName === sheetName) {
      const sourceColumn = columnDragState.dragging.column;
      const targetColumn = column;
      
      if (sourceColumn !== targetColumn) {
        onColumnReorder(sheetName, sourceColumn, targetColumn);
      }
    }
    
    setColumnDragState({ dragging: null, dropTarget: null });
  };

  const handleColumnDragEnd = () => {
    setColumnDragState({ dragging: null, dropTarget: null });
  };

  return (
    <Row gutter={[16, 16]}>
      {Object.keys(treeData).map((sheetName) => (
        <Col key={sheetName} className="col-12 col-sm-6 col-md-6 mb-4">
          <DraggableColumnCard
            sheetName={sheetName}
            tableObj={treeData[sheetName]}
            checkedColumns={checkedColumns}
            filters={filters}
            onCheck={onCheck}
            onFilterChange={onFilterChange}
            disabled={disabled}
            viewMode={viewMode}
            columnDragState={columnDragState}
            onColumnDragStart={handleColumnDragStart}
            onColumnDragOver={handleColumnDragOver}
            onColumnDrop={handleColumnDrop}
            onColumnDragEnd={handleColumnDragEnd}
            billType={billType}
          />
        </Col>
      ))}
    </Row>
  );
};